import React from 'react';
import { Controller, FieldValues, useFormState } from 'react-hook-form';
import { NumberInput } from '@heroui/react';
import { FormNumberInputProps } from './types';

const FormNumberInput = <TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  label,
  placeholder,
  isRequired = false,
  isDisabled = false,
  description,
  className,
  min,
  max,
  step,
  showErrorOnTouched = true,
  ...rest
}: FormNumberInputProps<TFieldValues>) => {
  const { touchedFields, errors, isSubmitted } = useFormState({ control });
  const fieldError = errors[name]?.message as string | undefined;
  const isTouched = touchedFields[name];
  const showError = showErrorOnTouched ? (isTouched || isSubmitted) && !!fieldError : !!fieldError;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const handleChange = ((value: number | React.ChangeEvent<HTMLInputElement>) => {
          // Handle both number and event types
          let numValue: number | undefined;

          if (typeof value === 'number') {
            numValue = value;
          } else if (value && 'target' in value && value.target) {
            // It's a DOM event, extract value from target
            const stringValue = (value.target as HTMLInputElement).value;
            numValue = stringValue === '' ? undefined : Number(stringValue);
          } else {
            field.onChange(undefined);
            return;
          }

          // Validate and convert the number
          if (numValue === null || numValue === undefined) {
            field.onChange(undefined);
            return;
          }

          // Ensure we have a valid number
          if (!isNaN(numValue) && isFinite(numValue)) {
            field.onChange(numValue);
          } else {
            field.onChange(undefined);
          }
        }) as ((value: number) => void) & React.ChangeEventHandler<HTMLInputElement>;

        return (
          <NumberInput
            {...rest}
            value={typeof field.value === 'number' ? field.value : field.value ?? undefined}
            onChange={handleChange}
            onBlur={field.onBlur}
            label={label}
            placeholder={placeholder}
            isRequired={isRequired}
            isDisabled={isDisabled}
            description={description}
            className={className}
            minValue={min}
            maxValue={max}
            step={step}
            isInvalid={showError}
            errorMessage={showError ? fieldError : undefined}
          />
        );
      }}
    />
  );
};

FormNumberInput.displayName = 'FormNumberInput';

export default FormNumberInput;
